import React, { useState, useEffect, useRef } from 'react';
import './AdminCategoriesPage.css';
import { ADMIN_API_URL } from '../services/api.config';

export default function AdminCategoriesPage({ userData }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRefs = useRef({});
  
  // New state for category edit modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    CategoryId: '',
    title: { en: '', ua: '' },
    tagline: { en: '', ua: '' }
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, [showHidden]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const url = `${ADMIN_API_URL}/admin/categories${showHidden ? '?showHidden=true' : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleHidden = async (categoryId, isCurrentlyHidden) => {
    try {
      let response;
      
      if (isCurrentlyHidden) {
        // Відновлюємо категорію
        response = await fetch(`${ADMIN_API_URL}/admin/categories/${categoryId}/recover`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userData.token}`
          }
        });
      } else {
        // Приховуємо категорію
        response = await fetch(`${ADMIN_API_URL}/admin/categories/${categoryId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userData.token}`
          }
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to ${isCurrentlyHidden ? 'recover' : 'hide'} category`);
      }

      // Оновлюємо список категорій
      fetchCategories();
    } catch (err) {
      console.error('Error toggling category visibility:', err);
      setError(err.message);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  // New functions for category edit modal
  const openCreateCategoryModal = () => {
    setEditMode(false);
    setCategoryFormData({
      CategoryId: '',
      title: { en: '', ua: '' },
      tagline: { en: '', ua: '' }
    });
    setFormError('');
    setFormSuccess('');
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setEditMode(true);
    setCategoryFormData({
      CategoryId: category.CategoryId,
      title: { ...category.title },
      tagline: { ...category.tagline }
    });
    setFormError('');
    setFormSuccess('');
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setFormError('');
    setFormSuccess('');
  };

  const handleFormChange = (e, field, lang) => {
    if (lang) {
      setCategoryFormData({
        ...categoryFormData,
        [field]: {
          ...categoryFormData[field],
          [lang]: e.target.value
        }
      });
    } else {
      setCategoryFormData({
        ...categoryFormData,
        [field]: e.target.value
      });
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setFormSubmitting(true);

    // Validation
    if (!editMode && !categoryFormData.CategoryId.trim()) {
      setFormError('Category ID is required');
      setFormSubmitting(false);
      return;
    }

    if (!categoryFormData.title.en.trim() || !categoryFormData.title.ua.trim()) {
      setFormError('Title is required in both languages');
      setFormSubmitting(false);
      return;
    }

    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode 
        ? `${ADMIN_API_URL}/admin/categories/${categoryFormData.CategoryId}`
        : `${ADMIN_API_URL}/admin/categories`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify(categoryFormData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save category');
      }

      setFormSuccess(editMode ? 'Category updated successfully' : 'Category created successfully');
      fetchCategories();
      
      // Close modal after short delay to show success message
      setTimeout(() => {
        closeCategoryModal();
      }, 1500);
    } catch (err) {
      console.error('Error saving category:', err);
      setFormError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFileInputChange = async (categoryId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Перевірка, чи файл має допустимий формат
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        [categoryId]: { success: false, message: 'Only PNG, JPEG, JPG and WEBP files are allowed' }
      });
      return;
    }

    try {
      setUploadStatus({
        ...uploadStatus,
        [categoryId]: { loading: true }
      });

      // Конвертація файлу в base64
      const base64Image = await convertFileToBase64(file);
      const base64String = base64Image.split(',')[1]; // Видаляємо префікс "data:image/png;base64,"

      // Відправка на сервер
      const response = await fetch(`${ADMIN_API_URL}/admin/categories/${categoryId}/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify({
          image: base64String
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }

      setUploadStatus({
        ...uploadStatus,
        [categoryId]: { success: true, message: 'Image uploaded successfully' }
      });

      // Оновлюємо список категорій
      fetchCategories();

      // Очищаємо input
      if (fileInputRefs.current[categoryId]) {
        fileInputRefs.current[categoryId].value = '';
      }

    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadStatus({
        ...uploadStatus,
        [categoryId]: { success: false, message: err.message }
      });
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const triggerFileInput = (categoryId) => {
    if (fileInputRefs.current[categoryId]) {
      fileInputRefs.current[categoryId].click();
    }
  };

  if (loading) {
    return <div className="admin-categories-loading">
      <div className="loading-spinner"></div>
      Loading categories...
    </div>;
  }

  if (error) {
    return <div className="admin-categories-error">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      Error: {error}
    </div>;
  }

  return (
    <div className="admin-categories-page">
      <div className="admin-categories-header">
        <h1>Categories Management</h1>
        <div className="admin-categories-actions">
          <label className="show-hidden-toggle">
            <input 
              type="checkbox" 
              checked={showHidden} 
              onChange={() => setShowHidden(!showHidden)} 
            />
            Show hidden categories
          </label>
        </div>
      </div>

      <div className="admin-categories-grid">
        {categories.length === 0 ? (
          <div className="no-categories">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
            <p>No categories found</p>
          </div>
        ) : (
          categories.map(category => (
            <div 
              key={category.CategoryId} 
              className={`category-card ${category.dontShow ? 'category-hidden' : ''}`}
            >
              {category.dontShow && (
                <div className="hidden-badge">Hidden Category</div>
              )}
              <div className="category-header">
                <h3>{category.title.en}</h3>
                <div className="category-actions">
                  <button 
                    className={`btn-toggle-visibility ${category.dontShow ? 'btn-recover' : 'btn-hide'}`}
                    onClick={() => handleToggleHidden(category.CategoryId, category.dontShow)}
                  >
                    {category.dontShow ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                        Recover
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                        Hide
                      </>
                    )}
                  </button>
                  <button 
                    className="btn-edit"
                    onClick={() => openEditCategoryModal(category)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit
                  </button>
                </div>
              </div>
              
              <div className="category-content">
                <div className="category-image-container">
                  <button 
                    className="btn-view-image"
                    onClick={() => handleImageClick(`${category.categoryImage}`)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    View Image
                  </button>
                  
                  <button 
                    className="btn-upload-image"
                    onClick={() => triggerFileInput(category.CategoryId)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    Upload New Image
                  </button>
                  
                  <input 
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="file-input-hidden"
                    ref={el => fileInputRefs.current[category.CategoryId] = el}
                    onChange={(e) => handleFileInputChange(category.CategoryId, e)}
                  />
                  
                  {uploadStatus[category.CategoryId] && (
                    <div className={`upload-status ${uploadStatus[category.CategoryId].success ? 'success' : 'error'}`}>
                      {uploadStatus[category.CategoryId].loading ? (
                        <div className="upload-loading">
                          <div className="upload-spinner"></div>
                          Uploading...
                        </div>
                      ) : (
                        uploadStatus[category.CategoryId].message
                      )}
                    </div>
                  )}
                </div>
                
                <div className="category-details">
                  <div className="category-id">
                    <strong>ID:</strong> {category.CategoryId}
                  </div>
                  
                  <div className="category-translations">
                    <div className="translation-section">
                      <h4>Title:</h4>
                      <div className="translation-item">
                        <span className="lang-badge">EN</span> {category.title.en}
                      </div>
                      <div className="translation-item">
                        <span className="lang-badge">UA</span> {category.title.ua}
                      </div>
                    </div>
                    
                    <div className="translation-section">
                      <h4>Tagline:</h4>
                      <div className="translation-item">
                        <span className="lang-badge">EN</span> {category.tagline.en}
                      </div>
                      <div className="translation-item">
                        <span className="lang-badge">UA</span> {category.tagline.ua}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="image-modal" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close-modal" onClick={closeModal}>&times;</span>
            <img src={selectedImage} alt="Category" />
          </div>
        </div>
      )}

      {/* Category Edit/Create Modal */}
      {showCategoryModal && (
        <div className="category-modal" onClick={closeCategoryModal}>
          <div className="category-modal-content" onClick={e => e.stopPropagation()}>
            <span className="close-modal" onClick={closeCategoryModal}>&times;</span>
            <h2>Edit Category</h2>
            
            <form onSubmit={handleCategorySubmit} className="category-form">
              <div className="form-group">
                <label htmlFor="categoryId">Category ID:</label>
                <input
                  type="text"
                  id="categoryId"
                  value={categoryFormData.CategoryId}
                  onChange={(e) => handleFormChange(e, 'CategoryId')}
                  disabled={editMode}
                  placeholder="e.g. laptop-cases"
                  required
                />
                {editMode && (
                  <div className="field-note">Category ID cannot be changed after creation</div>
                )}
              </div>

              <div className="form-group-title">Title</div>
              <div className="form-group">
                <label htmlFor="titleEn">
                  <span className="lang-badge">EN</span> Title (English):
                </label>
                <input
                  type="text"
                  id="titleEn"
                  value={categoryFormData.title.en}
                  onChange={(e) => handleFormChange(e, 'title', 'en')}
                  placeholder="English title"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="titleUa">
                  <span className="lang-badge">UA</span> Title (Ukrainian):
                </label>
                <input
                  type="text"
                  id="titleUa"
                  value={categoryFormData.title.ua}
                  onChange={(e) => handleFormChange(e, 'title', 'ua')}
                  placeholder="Ukrainian title"
                  required
                />
              </div>

              <div className="form-group-title">Tagline</div>
              <div className="form-group">
                <label htmlFor="taglineEn">
                  <span className="lang-badge">EN</span> Tagline (English):
                </label>
                <input
                  type="text"
                  id="taglineEn"
                  value={categoryFormData.tagline.en}
                  onChange={(e) => handleFormChange(e, 'tagline', 'en')}
                  placeholder="English tagline"
                />
              </div>

              <div className="form-group">
                <label htmlFor="taglineUa">
                  <span className="lang-badge">UA</span> Tagline (Ukrainian):
                </label>
                <input
                  type="text"
                  id="taglineUa"
                  value={categoryFormData.tagline.ua}
                  onChange={(e) => handleFormChange(e, 'tagline', 'ua')}
                  placeholder="Ukrainian tagline"
                />
              </div>

              {formError && (
                <div className="form-error">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {formError}
                </div>
              )}

              {formSuccess && (
                <div className="form-success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  {formSuccess}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={closeCategoryModal}
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={formSubmitting}
                >
                  {formSubmitting ? (
                    <>
                      <div className="btn-spinner"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Category'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 